import {
  PublicKey,
  TransactionInstruction,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
} from '@solana/web3.js';
import { 
	UNLINK_PROGRAM_CONFIG_AUTHORITY, 
	UNLINK_PROGRAM_ID as PROGRAM_ID 
} from '../../constants/general';
import { 
	DISCRIMINATOR_UPDATE_MIN_FEE,
	MINIMUM_FEE_PDA_SEED,
} from '../../constants/envelope';

/**
 * Creates a TransactionInstruction to update the on-chain minimum fee.
 * This instruction can only be successfully executed by the foundation authority.
 *
 * The fee is capped to 5000 lamports on-chain.
 *
 * @param authority - The foundation's signer PublicKey
 * @param newValue  - Proposed new minimum fee (u64, will be capped on-chain)
 * @returns TransactionInstruction
 */
export async function createUpdateMinimumFeeInstruction({
  authority,
  newValue,
}: {
  authority: PublicKey;
  newValue: number | bigint;
}): Promise<TransactionInstruction> {

	// Sanity check
  	if (typeof newValue !== 'number' && typeof newValue !== 'bigint') {
    	throw new Error('newValue must be a number or bigint');
  	}

  	if (BigInt(newValue) < 0n) {
    	throw new Error('Minimum fee must be non-negative');
  	}

  	if (BigInt(newValue) > 2n ** 64n - 1n) {
    	throw new Error('Minimum fee exceeds u64 range');
  	}

  	if (!authority || !authority.equals(UNLINK_PROGRAM_CONFIG_AUTHORITY)) {
		throw new Error('Invalid authority: only the foundation key may update the minimum fee');
	}

	// Derive PDA that stores the current minimum fee
	const [feePda] = await PublicKey.findProgramAddress(
		[Buffer.from(MINIMUM_FEE_PDA_SEED)],
		PROGRAM_ID
	);

	// Build instruction data: [0] discriminator, [1..9] u64 fee (LE)
	const instructionData = Buffer.alloc(9);
	instructionData.writeUInt8(DISCRIMINATOR_UPDATE_MIN_FEE, 0);
	instructionData.writeBigUInt64LE(BigInt(newValue), 1);

	return new TransactionInstruction({
		programId: PROGRAM_ID,
		keys: [
			{ pubkey: authority, isSigner: true, isWritable: true },
			{ pubkey: feePda, isSigner: false, isWritable: true },
			{ pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
			{ pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
		],
		data: instructionData,
	});
}